import React, { useRef, useState, useEffect } from 'react';
import { Award } from 'lucide-react';

const useInView = (threshold = 0.1) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setInView(true);
    }, { threshold });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, inView];
};

const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? 'translateY(0)' : 'translateY(40px)',
      transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`
    }}>
      {children}
    </div>
  );
};

const TeamPage = () => {
  const teamMembers = [
    { role: 'Teamledare', title: 'Point Person', desc: 'Analyserar hemmet, koordinerar teamet, säkerställer kvalitet. Bakgrund från sjukhusadministration.', skills: ['Kvalitetskontroll', 'Team Koordinering', 'Kundkommunikation'], img: 'https://placehold.co/400x300/1a1a2e/C9A84C?text=GALAX' },
    { role: 'Badrumsspecialist', title: 'Hygiene Expert', desc: 'Specialiserad på hygieniska utrymmen. Utbildad i sjukhushygien där precision är kritisk.', skills: ['Desinfektion', 'Sanitetsrengöring', 'Fukt & Mögel'], img: 'https://placehold.co/400x300/1a1a2e/C9A84C?text=GALAX' },
    { role: 'Köksspecialist', title: 'Kitchen Master', desc: 'Expert på kök och matlagningsytor. Erfarenhet från skolkök med höga säkerhetskrav.', skills: ['Fettborttagning', 'Ugnsrengöring', 'HACCP-Standard'], img: 'https://placehold.co/400x300/1a1a2e/C9A84C?text=GALAX' },
    { role: 'Detaljerare', title: 'The Finisher', desc: 'Slutkontrollerar varje rum. Ser det andra missar. Bakgrund från hotellbranschen.', skills: ['Kvalitetsinspektion', 'Detaljarbete', 'Slutkontroll'], img: 'https://placehold.co/400x300/1a1a2e/C9A84C?text=GALAX' }
  ];

  return (
    <div style={{ backgroundColor: '#f8f7f4', color: '#1a1a2e' }}>
      {/* Hero */}
      <section className="relative py-32" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1600&q=80')`,
          backgroundSize: 'cover', backgroundPosition: 'center'
        }}/>
        <div className="absolute top-0 left-0 w-full h-1" style={{ background: 'linear-gradient(90deg, transparent, #C9A84C, transparent)' }}/>
        <div className="relative z-10 text-center text-white px-4">
          <p className="text-sm font-bold tracking-widest mb-4" style={{ color: '#C9A84C' }}>VÅRT TEAM</p>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Möt <span style={{ color: '#C9A84C' }}>Elite Teamet</span></h1>
          <p className="text-xl max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.8)' }}>
            Vi är inte bara städare — vi är specialister som tränat för att bli Motalas bästa städteam.
          </p>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-24 px-4" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&q=80"
                alt="Team working" className="rounded-2xl shadow-2xl w-full object-cover" style={{ height: '450px' }}/>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <p className="text-sm font-bold tracking-widest mb-3" style={{ color: '#C9A84C' }}>HUR VI ARBETAR</p>
              <h2 className="text-4xl font-bold mb-6" style={{ color: '#1a1a2e' }}>Synkroniserat & Effektivt</h2>
              <div className="space-y-4 mb-6" style={{ color: '#555' }}>
                <p>Ett GALAX-team består av 2-4 specialister beroende på hemmets storlek. Varje specialist har sin dedikerade roll och expertis.</p>
                <p>Vi tränar grundligt (vid behov upp till 6 veckor) innan vi städar vårt första hem. Under denna tid lär vi oss att:</p>
              </div>
              <ul className="space-y-3">
                {['Kommunicera tyst och effektivt', 'Röra oss synkroniserat genom hemmet', 'Dela utrymmen utan att krocka', 'Hålla exakt samma kvalitetsstandard'].map(item => (
                  <li key={item} className="flex items-center space-x-3">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#C9A84C' }}>
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4L3.5 6.5L9 1" stroke="#1a1a2e" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <span style={{ color: '#333' }}>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 p-4 rounded-xl" style={{ backgroundColor: '#1a1a2e' }}>
                <p className="font-bold" style={{ color: '#C9A84C' }}>Resultatet: 40% snabbare än traditionella städare utan att kompromissa kvalitet.</p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Team Roles */}
      <section className="py-24 px-4" style={{ backgroundColor: '#f8f7f4' }}>
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center mb-16">
            <p className="text-sm font-bold tracking-widest mb-3" style={{ color: '#C9A84C' }}>SPECIALISTERNA</p>
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#1a1a2e' }}>Rollerna i Teamet</h2>
            <div className="w-16 h-1 mx-auto" style={{ backgroundColor: '#C9A84C' }}/>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {teamMembers.map((member, idx) => (
              <AnimatedSection key={idx} delay={idx * 0.1}>
                <div className="rounded-2xl overflow-hidden h-full transition-all hover:scale-105"
                  style={{ backgroundColor: '#fff', border: '1px solid #e8e4d9', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
                  <div className="h-48 overflow-hidden">
                    <img src={member.img} alt={member.title} className="w-full h-full object-cover"/>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold tracking-wider" style={{ color: '#C9A84C' }}>{member.role}</span>
                      <Award className="w-4 h-4" style={{ color: '#C9A84C' }}/>
                    </div>
                    <h3 className="text-lg font-bold mb-2" style={{ color: '#1a1a2e' }}>{member.title}</h3>
                    <p className="text-sm mb-4" style={{ color: '#777' }}>{member.desc}</p>
                    <div className="flex flex-wrap gap-1">
                      {member.skills.map((skill, i) => (
                        <span key={i} className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#f8f7f4', color: '#1a1a2e', border: '1px solid #e8e4d9' }}>{skill}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Training */}
      <section className="py-24 px-4" style={{ backgroundColor: '#1a1a2e' }}>
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center mb-16">
            <p className="text-sm font-bold tracking-widest mb-3" style={{ color: '#C9A84C' }}>VÅR TRÄNING</p>
            <h2 className="text-4xl font-bold mb-4 text-white">Träningsprogrammet <span className="text-sm font-normal" style={{ color: 'rgba(255,255,255,0.5)' }}>(vid behov)</span></h2>
            <div className="w-16 h-1 mx-auto" style={{ backgroundColor: '#C9A84C' }}/>
          </AnimatedSection>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { weeks: 'Vecka 1-2', title: 'Individuell Mastery', desc: 'Varje städare lär sig grunderna: teknik, produkter, verktyg och kvalitetsstandarder.' },
              { weeks: 'Vecka 3-4', title: 'Team Synkronisering', desc: 'Vi tränar tillsammans — lär oss kommunicera tyst, dela utrymmen och arbeta som en enhet.' },
              { weeks: 'Vecka 5-6', title: 'Verkliga Scenarion', desc: 'Vi städar testhem under tidspress, hanterar överraskningar och perfektionerar vår process.' }
            ].map((phase, idx) => (
              <AnimatedSection key={idx} delay={idx * 0.1}>
                <div className="p-8 rounded-2xl" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(201,168,76,0.2)' }}>
                  <div className="text-3xl font-bold mb-2" style={{ color: '#C9A84C' }}>{phase.weeks}</div>
                  <h3 className="text-xl font-bold mb-3 text-white">{phase.title}</h3>
                  <p style={{ color: 'rgba(255,255,255,0.6)' }}>{phase.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default TeamPage;